import { mapFulfillmentError } from '../lib/store-fulfillment';
import type { Json } from '../supabase/database.types';
import type { CreateAddressOrderInput, DeliveryMethod } from '../types/order';
/**
 * F-017: order service layer.
 *
 * Wraps the `create_order_from_cart` SECURITY DEFINER RPC. Mirrors
 * `cart.service.ts` shape — single-row-array unwrap for `RETURNS TABLE`
 * RPCs, `r_` prefix stripping (F-014 lesson), throws on auth/network
 * errors, returns business outcomes as data.
 *
 * Error mapping delegates to a whitelist classifier (similar to
 * `mapCartError`) that returns i18n keys from the `OrderErrorKey` union.
 *
 * Design: .dukanti/research/stitch-F-017-submit-order/02-design.md §Shared Contract
 */
import type { Database, SupabaseClient } from '../supabase/client';
import type {
  AdminOrderDetail,
  AdminOrder,
  ManualApprovalFields,
  AdminOrderSellableVariant,
  AdminPreparedOrderErrorKey,
  CreateOrderInput,
  CreateAdminPreparedOrderInput,
  CreateAdminPreparedOrderResult,
  CreateOrderResult,
  ListStoreOrdersOptions,
  ListStoreOrdersPage,
  Order,
  OrderEvent,
  OrderItem,
  OrderErrorKey,
  OrderHistoryRow,
  OrderCountSummary,
  OrderStatus,
  StoreOrderRow,
  StoreOrderSortBy,
  SearchAdminOrderSellableVariantsInput,
} from '../types/order';
import { CUSTOMER_ACTIVE_ORDER_STATUSES } from '../types/order';
import type { CourierOrderAssignmentSummary } from '../types/courier';
import { SUPPORTED_ADDRESS_COUNTRY_CODE } from '../types/customer-address';

type CreateOrderRpcArgs =
  Database['public']['Functions']['create_order_from_cart_fulfillment_v1']['Args'];

interface SearchAdminSellableRpcArgs {
  readonly target_store_id: string;
  readonly search_query?: string;
  readonly result_limit?: number;
  readonly result_offset?: number;
  readonly target_category_id?: string | null;
}

type CreateAdminPreparedOrderRpcArgs =
  Database['public']['Functions']['create_admin_prepared_order_fulfillment_v1']['Args'];

interface CreateAdminPreparedOrderRpcRow {
  readonly r_success: boolean;
  readonly r_order_id: string | null;
  readonly r_error_code: string | null;
  readonly r_unavailable_variant_ids: string[] | null;
  readonly r_unavailable_line_messages: string[] | null;
}

type ManualApprovalRow = {
  readonly order_id: string;
  readonly status: 'pending' | 'approved' | 'declined';
  readonly required_at: string;
  readonly approved_at: string | null;
  readonly approved_by_user_id: string | null;
  readonly declined_at: string | null;
  readonly declined_by_user_id: string | null;
};

type AwaitingApprovalOrderRow = {
  readonly id: string;
  readonly status: string;
  readonly created_at: string;
  delivery_method: DeliveryMethod;
  delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  delivery_fee_cents: number | null;
  total_cents: number | null;
  fulfillment_version: 1 | null;
  readonly subtotal_cents: number;
  readonly currency: string;
  readonly customer_name_snapshot: string;
  readonly customer_phone_snapshot: string | null;
  readonly item_count: number;
  readonly manual_approval_status: 'pending';
  readonly manual_approval_required_at: string;
  readonly manual_approved_at: string | null;
  readonly manual_approved_by_user_id: string | null;
  readonly manual_declined_at: string | null;
  readonly manual_declined_by_user_id: string | null;
};

const CUSTOMER_ORDER_SELECT =
  'id, user_id, store_id, status, delivery_method, delivery_locality_id, delivery_locality_name_ar_snapshot, delivery_locality_name_he_snapshot, delivery_locality_name_en_snapshot, delivery_locality_symbol_number_snapshot, customer_name_snapshot, customer_phone_snapshot, address_label_snapshot, address_recipient_snapshot, address_line_1_snapshot, address_line_2_snapshot, address_city_snapshot, address_region_snapshot, address_postal_code_snapshot, address_country_code_snapshot, address_phone_snapshot, delivery_latitude, delivery_longitude, delivery_pricing_state, delivery_fee_cents, total_cents, fulfillment_version, delivery_rate_revision, pickup_place_id_snapshot, pickup_place_name_snapshot, pickup_place_details_snapshot, subtotal_cents, currency, customer_notes, shipped_at, delivered_at, cancelled_at, rejected_at, created_at, updated_at';

function manualApprovalFieldsFromRow(row: ManualApprovalRow | null): ManualApprovalFields {
  if (!row) {
    return {
      manual_approval_status: 'not_required',
      manual_approval_required_at: null,
      manual_approved_at: null,
      manual_approved_by_user_id: null,
      manual_declined_at: null,
      manual_declined_by_user_id: null,
    };
  }
  return {
    manual_approval_status: row.status,
    manual_approval_required_at: row.required_at,
    manual_approved_at: row.approved_at,
    manual_approved_by_user_id: row.approved_by_user_id,
    manual_declined_at: row.declined_at,
    manual_declined_by_user_id: row.declined_by_user_id,
  };
}

function clampAdminSellableLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return 50;
  return Math.min(Math.max(Math.trunc(limit), 1), 100);
}

function normalizeAdminSellableOffset(offset: number | undefined): number {
  if (typeof offset !== 'number' || !Number.isFinite(offset)) return 0;
  return Math.max(Math.trunc(offset), 0);
}

export async function searchAdminOrderSellableVariants(
  client: SupabaseClient,
  input: SearchAdminOrderSellableVariantsInput,
): Promise<AdminOrderSellableVariant[]> {
  const args: SearchAdminSellableRpcArgs = {
    target_store_id: input.storeId,
    search_query: input.query.trim(),
    result_limit: clampAdminSellableLimit(input.limit),
    result_offset: normalizeAdminSellableOffset(input.offset),
    target_category_id: input.categoryId ?? null,
  };
  const { data, error } = await client.rpc('search_admin_order_sellable_variants', args as never);
  if (error) throw error;
  return (data ?? []) as unknown as AdminOrderSellableVariant[];
}

export async function createAdminPreparedOrder(
  client: SupabaseClient,
  input: CreateAdminPreparedOrderInput,
): Promise<CreateAdminPreparedOrderResult> {
  const args: CreateAdminPreparedOrderRpcArgs = {
    target_store_id: input.storeId,
    customer_name: input.customerName,
    customer_phone: input.customerPhone,
    fulfillment: { ...input.fulfillment },
    acceptance: input.acceptance as unknown as Json,
    delivery_info:
      input.fulfillment.method === 'delivery' && 'deliveryLocalityId' in input
        ? { localityId: input.deliveryLocalityId, addressLine1: input.addressLine1 }
        : null,
    customer_notes: input.customerNotes ?? '',
    lines: input.lines.map((line) => ({
      variant_id: line.variant_id,
      quantity: line.quantity,
    })),
  };
  const { data, error } = await client.rpc('create_admin_prepared_order_fulfillment_v1', args);
  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    throw new Error('createAdminPreparedOrder: empty result from create_admin_prepared_order RPC');
  }

  const typed = row as CreateAdminPreparedOrderRpcRow;
  return {
    success: typed.r_success,
    order_id: typed.r_order_id,
    error_code: (typed.r_error_code as CreateAdminPreparedOrderResult['error_code']) ?? null,
    unavailable_variant_ids: typed.r_unavailable_variant_ids ?? [],
    unavailable_line_messages: typed.r_unavailable_line_messages ?? [],
  };
}

export function mapAdminPreparedOrderError(
  err: unknown,
  rpcResult?: { success: boolean; error_code: string | null },
): AdminPreparedOrderErrorKey {
  if (rpcResult && !rpcResult.success) {
    const fulfillmentKey = mapFulfillmentError(rpcResult.error_code);
    if (fulfillmentKey) return fulfillmentKey;
    switch (rpcResult.error_code) {
      case 'NO_LINES':
        return 'admin.orders.prepare.errors.noLines';
      case 'QUANTITY_INVALID':
        return 'admin.orders.prepare.errors.quantityInvalid';
      case 'INSUFFICIENT_STOCK':
        return 'admin.orders.prepare.errors.insufficientStock';
      case 'PRODUCT_UNAVAILABLE':
        return 'admin.orders.prepare.errors.deletedProduct';
      case 'LOCALITY_INVALID':
        return 'admin.orders.prepare.errors.localityInvalid';
      case 'PHONE_INVALID':
        return 'admin.orders.prepare.errors.phoneInvalid';
      case 'ADDRESS_REQUIRED':
        return 'admin.orders.prepare.errors.addressRequired';
      case 'ADDRESS_TOO_LONG':
        return 'admin.orders.prepare.errors.addressTooLong';
      case 'CUSTOMER_NAME_REQUIRED':
        return 'admin.orders.prepare.errors.customerNameRequired';
      case 'UNAUTHORIZED':
        return 'admin.orders.prepare.errors.unauthorized';
      case 'VARIANT_UNAVAILABLE':
      case 'STORE_UNAVAILABLE':
        return 'admin.orders.prepare.errors.variantUnavailable';
      default:
        return 'admin.orders.prepare.errors.unknown';
    }
  }

  if (!err || typeof err !== 'object') return 'admin.orders.prepare.errors.unknown';
  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    return 'admin.orders.prepare.errors.networkFailed';
  }

  const maybe = err as {
    readonly code?: string;
    readonly message?: string;
    readonly name?: string;
  };
  if (maybe.name === 'AuthRetryableFetchError') return 'admin.orders.prepare.errors.networkFailed';
  if (maybe.code === '42501') return 'admin.orders.prepare.errors.unauthorized';
  if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
    return 'admin.orders.prepare.errors.networkFailed';
  }
  return 'admin.orders.prepare.errors.unknown';
}

/**
 * Calls the `create_order_from_cart` SECURITY DEFINER RPC.
 * Unwraps RETURNS TABLE single-row array, strips r_ prefix.
 * Throws on auth/network errors; returns business failures as data.
 */
export async function createOrderFromCart(
  client: SupabaseClient,
  input: CreateOrderInput,
): Promise<CreateOrderResult> {
  const delivery: CreateAddressOrderInput | null =
    input.fulfillment.method === 'delivery' && 'addressRecipient' in input ? input : null;
  if (
    delivery &&
    delivery.addressCountryCode.trim().toUpperCase() !== SUPPORTED_ADDRESS_COUNTRY_CODE
  ) {
    throw new Error('createOrderFromCart: addressCountryCode must be IL');
  }
  const rpcArgs = {
    target_store_id: input.storeId,
    fulfillment: { ...input.fulfillment },
    acceptance: input.acceptance as unknown as Json,
    customer_notes: input.customerNotes,
    delivery_info: delivery
      ? {
          recipient: delivery.addressRecipient,
          line_1: delivery.addressLine1,
          line_2: delivery.addressLine2,
          city: delivery.addressCity,
          region: delivery.addressRegion,
          postal_code: delivery.addressPostalCode,
          country_code: SUPPORTED_ADDRESS_COUNTRY_CODE,
          phone: delivery.addressPhone,
          label: delivery.addressLabel,
          latitude: delivery.deliveryLatitude ?? null,
          longitude: delivery.deliveryLongitude ?? null,
          localityId: delivery.deliveryLocalityId,
        }
      : null,
  } as unknown as CreateOrderRpcArgs;
  const { data, error } = await client.rpc('create_order_from_cart_fulfillment_v1', rpcArgs);
  if (error) throw error;

  // RETURNS TABLE(...) comes back as `[{ ... }]`. Service unwraps for
  // caller ergonomics. The RPC's columns are r_*-prefixed to avoid
  // PL/pgSQL 42702 ambiguity — this service strips the prefix so the
  // CreateOrderResult surface stays stable across consumers.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    throw new Error('createOrderFromCart: empty result from create_order_from_cart RPC');
  }
  const typed = row as {
    r_success: boolean;
    r_order_id: string | null;
    r_error_code: string | null;
    r_unavailable_product_ids: string[];
    r_unavailable_product_names: string[];
    r_unavailable_variant_ids?: string[];
    r_unavailable_line_messages?: string[];
  };
  return {
    success: typed.r_success,
    order_id: typed.r_order_id,
    error_code: (typed.r_error_code as CreateOrderResult['error_code']) ?? null,
    unavailable_product_ids: typed.r_unavailable_product_ids ?? [],
    unavailable_product_names: typed.r_unavailable_product_names ?? [],
    unavailable_variant_ids: typed.r_unavailable_variant_ids ?? [],
    unavailable_line_messages: typed.r_unavailable_line_messages ?? [],
  };
}

/**
 * Whitelist error-to-i18n-key mapper.
 * When `rpcResult` is provided and `success=false`, maps error_code to key.
 * Otherwise delegates to generic network/auth error classification.
 */
export function mapOrderError(
  err: unknown,
  rpcResult?: { success: boolean; error_code: string | null },
): OrderErrorKey {
  // Branch 1: business error returned as data (not thrown)
  if (rpcResult && !rpcResult.success) {
    const fulfillmentKey = mapFulfillmentError(rpcResult.error_code);
    if (fulfillmentKey) return fulfillmentKey;
    switch (rpcResult.error_code) {
      case 'PRODUCT_UNAVAILABLE':
        return 'order.errors.productUnavailable';
      case 'VARIANT_UNAVAILABLE':
        return 'order.errors.productUnavailable';
      case 'CART_EMPTY':
        return 'order.errors.cartEmpty';
      case 'STORE_UNAVAILABLE':
        return 'order.errors.storeUnavailable';
      default:
        return 'order.errors.unknown';
    }
  }

  // Branch 2: thrown error (network / auth / programmer)
  if (!err || typeof err !== 'object') return 'order.errors.unknown';

  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'order.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
  };

  if (maybe.name === 'AuthRetryableFetchError') return 'order.errors.networkFailed';

  switch (maybe.code) {
    case '42501':
      return 'order.errors.unknown';
    case 'P0002':
      return 'order.errors.unknown';
    default:
      break;
  }

  // Network-class errors from fetch failures
  if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
    return 'order.errors.networkFailed';
  }

  return 'order.errors.unknown';
}

// ── F-018 additions ─────────────────────────────────────────────────

/**
 * Fetch an order with its items in a single joined query.
 * Returns `{ order, items }`. Throws on error.
 */
export async function getOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<{ order: Order; items: OrderItem[] }> {
  const { data, error } = await client
    .from('orders')
    .select(`${CUSTOMER_ORDER_SELECT}, order_items(*)`)
    .eq('id', orderId)
    .single();

  if (error) throw error;

  // Supabase returns the nested relation as an array under the
  // foreign-table key. Extract it and return the parent row without it.
  const { order_items: rawItems, ...orderRow } = data as Record<string, unknown> & {
    order_items: unknown[];
  };

  return {
    order: orderRow as unknown as Order,
    items: (rawItems ?? []) as unknown as OrderItem[],
  };
}

/**
 * Cancel an ordered order via the `customer_cancel_order` SECURITY DEFINER RPC.
 * Throws on error (auth, not-found, wrong-status, network).
 */
export async function cancelOrder(client: SupabaseClient, orderId: string): Promise<void> {
  const { error } = await client.rpc('customer_cancel_order', {
    target_order_id: orderId,
  });
  if (error) throw error;
}

/**
 * Map cancel-specific RPC errors to i18n keys.
 * P0002 = order not found, P0003 = wrong status, default = generic cancel failure.
 */
export function mapCancelOrderError(err: unknown): OrderErrorKey {
  if (!err || typeof err !== 'object') return 'order.errors.cancelFailed';

  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'order.errors.cancelFailed';
  }

  const maybe = err as { code?: string; message?: string; name?: string };

  if (maybe.name === 'AuthRetryableFetchError') return 'order.errors.cancelFailed';

  switch (maybe.code) {
    case 'P0002':
      return 'order.errors.orderNotFound';
    case 'P0003':
      return 'order.errors.cancelAlreadyProcessed';
    default:
      break;
  }

  if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
    return 'order.errors.cancelFailed';
  }

  return 'order.errors.cancelFailed';
}

// ── F-019 additions ─────────────────────────────────────────────────

export interface ListOrdersOptions {
  readonly limit: number;
  readonly offset: number;
  readonly status: OrderStatus | null; // null = all statuses
  readonly userId: string;
}

export interface ListOrdersPage {
  readonly rows: OrderHistoryRow[];
  readonly hasMore: boolean;
}

/**
 * Fetch a page of orders for the authenticated customer.
 * Fetches `limit + 1` rows to determine if there are more pages.
 * Derives `item_count` from the PostgREST embedded resource count (`order_items(count)`).
 * PostgREST returns `[{ count: N }]` (array with single aggregate object) for forward
 * one-to-many relationships — accessor is `row.order_items?.[0]?.count ?? 0`.
 * Throws on Supabase error (never returns `{ data, error }`).
 */
export async function listOrders(
  client: SupabaseClient,
  options: ListOrdersOptions,
): Promise<ListOrdersPage> {
  const { limit, offset, status, userId } = options;
  // Fetch limit + 1 to detect whether there is a next page.
  const fetchLimit = limit + 1;

  let query = client
    .from('orders')
    .select(
      'id, status, created_at, delivery_method, delivery_pricing_state, delivery_fee_cents, total_cents, fulfillment_version, subtotal_cents, currency, store_id, stores(name, logo_url, logo_storage_path), order_items(count)',
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + fetchLimit - 1);

  if (status !== null) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rawRows = (data ?? []) as Array<{
    id: string;
    status: string;
    created_at: string;
    delivery_method: DeliveryMethod;
    delivery_pricing_state: 'legacy_unconfigured' | 'priced';
    delivery_fee_cents: number | null;
    total_cents: number | null;
    fulfillment_version: 1 | null;
    subtotal_cents: number;
    currency: string;
    store_id: string;
    stores: { name: string; logo_url: string | null; logo_storage_path: string | null } | null;
    order_items: { count: number }[] | null;
  }>;

  const hasMore = rawRows.length > limit;
  const pageRows = hasMore ? rawRows.slice(0, limit) : rawRows;

  const rows: OrderHistoryRow[] = pageRows.map((row) => ({
    id: row.id,
    status: row.status as OrderStatus,
    created_at: row.created_at,
    delivery_method: row.delivery_method,
    delivery_pricing_state: row.delivery_pricing_state,
    delivery_fee_cents: row.delivery_fee_cents,
    total_cents: row.total_cents,
    fulfillment_version: row.fulfillment_version,
    subtotal_cents: row.subtotal_cents,
    currency: row.currency,
    store_id: row.store_id,
    store: row.stores ?? null,
    item_count: row.order_items?.[0]?.count ?? 0,
  }));

  return { rows, hasMore };
}

/**
 * Count total and active (ordered + shipped) orders for a customer.
 * Fires two lightweight HEAD count queries. Throws on error.
 */
export async function countOrdersSummary(
  client: SupabaseClient,
  userId: string,
): Promise<OrderCountSummary> {
  const { count: totalCount, error: totalError } = await client
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (totalError) throw totalError;

  const { count: activeCount, error: activeError } = await client
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .in('status', [...CUSTOMER_ACTIVE_ORDER_STATUSES]);

  if (activeError) throw activeError;

  return { total: totalCount ?? 0, active: activeCount ?? 0 };
}

// ── F-021 additions ─────────────────────────────────────────────────

/**
 * Fetch a page of orders for a merchant's store.
 * Fetches `limit + 1` rows to determine if there are more pages.
 * Derives `item_count` from the PostgREST embedded resource count (`order_items(count)`).
 * PostgREST returns `[{ count: N }]` (array with single aggregate object) for forward
 * one-to-many relationships — accessor is `row.order_items?.[0]?.count ?? 0`.
 * Throws on Supabase error (never returns `{ data, error }`).
 */
export async function listStoreOrders(
  client: SupabaseClient,
  options: ListStoreOrdersOptions,
): Promise<ListStoreOrdersPage> {
  const { limit, offset, status, reviewFilter, storeId, sortBy, search } = options;
  // Fetch limit + 1 to detect whether there is a next page.
  const fetchLimit = limit + 1;

  if (reviewFilter === 'awaiting_approval') {
    return listStoreOrdersAwaitingApproval(client, {
      limit,
      fetchLimit,
      offset,
      storeId,
      sortBy,
      search,
    });
  }

  let query = client
    .from('orders')
    .select(
      'id, status, created_at, delivery_method, delivery_pricing_state, delivery_fee_cents, total_cents, fulfillment_version, subtotal_cents, currency, customer_name_snapshot, customer_phone_snapshot, order_items(count)',
    )
    .eq('store_id', storeId);

  // Apply status filter before search/range.
  if (status !== null) {
    query = query.eq('status', status);
  }

  // Apply search filter (name, phone, or exact UUID match).
  if (search !== null && search.trim() !== '') {
    const q = search.trim();
    // UUID heuristic: 36 chars with dashes.
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(q);
    const orClause = looksLikeUuid
      ? `customer_name_snapshot.ilike.%${q}%,customer_phone_snapshot.ilike.%${q}%,id.eq.${q}`
      : `customer_name_snapshot.ilike.%${q}%,customer_phone_snapshot.ilike.%${q}%`;
    query = query.or(orClause);
  }

  // Apply server-side sort.
  switch (sortBy) {
    case 'total_asc':
    case 'total_desc':
      query = query
        .order('total_cents', { ascending: sortBy === 'total_asc', nullsFirst: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });
      break;
    case 'created_at_desc':
      query = query.order('created_at', { ascending: false });
      break;
    case 'created_at_asc':
      query = query.order('created_at', { ascending: true });
      break;
    case 'subtotal_desc':
      query = query.order('subtotal_cents', { ascending: false });
      break;
    case 'subtotal_asc':
      query = query.order('subtotal_cents', { ascending: true });
      break;
    default:
      query = query.order('created_at', { ascending: false });
  }

  query = query.range(offset, offset + fetchLimit - 1);

  const { data, error } = await query;
  if (error) throw error;

  const rawRows = (data ?? []) as Array<{
    id: string;
    status: string;
    created_at: string;
    delivery_method: DeliveryMethod;
    delivery_pricing_state: 'legacy_unconfigured' | 'priced';
    delivery_fee_cents: number | null;
    total_cents: number | null;
    fulfillment_version: 1 | null;
    subtotal_cents: number;
    currency: string;
    customer_name_snapshot: string;
    customer_phone_snapshot: string | null;
    order_items: { count: number }[] | null;
  }>;

  const hasMore = rawRows.length > limit;
  const pageRows = hasMore ? rawRows.slice(0, limit) : rawRows;

  const rows: StoreOrderRow[] = pageRows.map((row) => ({
    id: row.id,
    status: row.status as OrderStatus,
    created_at: row.created_at,
    delivery_method: row.delivery_method,
    delivery_pricing_state: row.delivery_pricing_state,
    delivery_fee_cents: row.delivery_fee_cents,
    total_cents: row.total_cents,
    fulfillment_version: row.fulfillment_version,
    subtotal_cents: row.subtotal_cents,
    currency: row.currency,
    customer_name_snapshot: row.customer_name_snapshot,
    customer_phone_snapshot: row.customer_phone_snapshot,
    item_count: row.order_items?.[0]?.count ?? 0,
    ...manualApprovalFieldsFromRow(null),
  }));

  const approvalFields = await listManualApprovalFields(
    client,
    storeId,
    rows.map((row) => row.id),
  );

  const assignments = await listStoreOrderCourierAssignments(
    client,
    storeId,
    rows.map((row) => row.id),
  );

  return {
    rows: rows.map((row) => ({
      ...row,
      ...(approvalFields.get(row.id) ?? manualApprovalFieldsFromRow(null)),
      courier_assignment: assignments.get(row.id) ?? null,
    })),
    hasMore,
  };
}

async function listStoreOrdersAwaitingApproval(
  client: SupabaseClient,
  options: {
    readonly limit: number;
    readonly fetchLimit: number;
    readonly offset: number;
    readonly storeId: string;
    readonly sortBy: StoreOrderSortBy;
    readonly search: string | null;
  },
): Promise<ListStoreOrdersPage> {
  const { limit, fetchLimit, offset, storeId, sortBy, search } = options;
  const normalizedSearch = search?.trim() ? search.trim() : null;
  const { data, error } = await client.rpc(
    'list_store_orders_awaiting_manual_approval_fulfillment_v1',
    {
      target_store_id: storeId,
      result_limit: fetchLimit,
      result_offset: offset,
      sort_by: sortBy,
      search_query: normalizedSearch,
    } as never,
  );
  if (error) throw error;

  const rawRows = (data ?? []) as AwaitingApprovalOrderRow[];
  const hasMore = rawRows.length > limit;
  const pageRows = hasMore ? rawRows.slice(0, limit) : rawRows;

  const rows: StoreOrderRow[] = pageRows.map((row) => ({
    id: row.id,
    status: row.status as OrderStatus,
    created_at: row.created_at,
    delivery_method: row.delivery_method,
    delivery_pricing_state: row.delivery_pricing_state,
    delivery_fee_cents: row.delivery_fee_cents,
    total_cents: row.total_cents,
    fulfillment_version: row.fulfillment_version,
    subtotal_cents: row.subtotal_cents,
    currency: row.currency,
    customer_name_snapshot: row.customer_name_snapshot,
    customer_phone_snapshot: row.customer_phone_snapshot,
    item_count: row.item_count,
    manual_approval_status: row.manual_approval_status,
    manual_approval_required_at: row.manual_approval_required_at,
    manual_approved_at: row.manual_approved_at,
    manual_approved_by_user_id: row.manual_approved_by_user_id,
    manual_declined_at: row.manual_declined_at,
    manual_declined_by_user_id: row.manual_declined_by_user_id,
  }));

  const assignments = await listStoreOrderCourierAssignments(
    client,
    storeId,
    rows.map((row) => row.id),
  );

  return {
    rows: rows.map((row) => ({
      ...row,
      courier_assignment: assignments.get(row.id) ?? null,
    })),
    hasMore,
  };
}

// ── F-024 additions ─────────────────────────────────────────────────

/**
 * Count incoming ordered orders for a merchant's store.
 * HEAD count query — no rows returned, only the count.
 * Throws on Supabase error. Returns 0 if count is null (empty set).
 * RLS enforces store membership; no manual userId check needed.
 */
export async function countIncomingOrders(
  client: SupabaseClient,
  storeId: string,
): Promise<number> {
  const { count, error } = await client
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('store_id', storeId)
    .eq('status', 'ordered');

  if (error) throw error;
  return count ?? 0;
}

// ── F-024-multistore-badge followup ─────────────────────────────────────────

/**
 * Count incoming ordered orders across ALL stores the authenticated user is a member of.
 * Calls the `count_incoming_orders_for_user` SECURITY DEFINER RPC which
 * internally joins store_members → orders in one server-side query.
 *
 * The RPC enforces auth.uid() === userId, so callers cannot query other users.
 *
 * @param client - authenticated Supabase client
 * @param userId - the authenticated user's UUID (auth.uid())
 */
export async function countIncomingOrdersForUser(
  client: SupabaseClient,
  userId: string,
): Promise<number> {
  const { data, error } = await client.rpc('count_incoming_orders_for_user', {
    user_id_param: userId,
  });
  if (error) throw error;
  return (data as unknown as number) ?? 0;
}

export async function countOrdersNeedingAttentionForUser(
  client: SupabaseClient,
  userId: string,
): Promise<number> {
  const { data, error } = await client.rpc('count_orders_needing_attention_for_user', {
    user_id_param: userId,
  } as never);
  if (error) throw error;
  return (data as unknown as number) ?? 0;
}

// F-022 additions ────────────────────────────────────────────────────

/**
 * Fetch a full order with its items and events for admin detail view.
 * DO NOT modify getOrder (line ~138) — it is consumed by storefront
 * F-018/F-020. See Decision 3 in 01-requirements.md.
 *
 * RLS: orders SELECT policy requires is_store_member(store_id).
 * PGRST116 (no rows) is thrown as an error when RLS rejects the request
 * — callers treat it as not-found/unauthorized.
 *
 * order_events are sorted ascending by created_at for timeline display.
 */
export async function getAdminOrderDetail(
  client: SupabaseClient,
  orderId: string,
): Promise<AdminOrderDetail> {
  const { data, error } = await client
    .from('orders')
    .select(
      `${CUSTOMER_ORDER_SELECT}, order_items(*), order_events(id, order_id, from_status, to_status, actor_user_id, metadata, created_at)`,
    )
    .eq('id', orderId)
    .order('created_at', { referencedTable: 'order_events', ascending: true })
    .single();

  if (error) throw error;

  const {
    order_items: rawItems,
    order_events: rawEvents,
    ...orderRow
  } = data as Record<string, unknown> & {
    order_items: unknown[];
    order_events: unknown[];
  };

  const typedOrder = orderRow as unknown as Order;
  const approvalFields = await listManualApprovalFields(client, typedOrder.store_id, [
    typedOrder.id,
  ]);
  const adminOrder: AdminOrder = {
    ...typedOrder,
    ...(approvalFields.get(typedOrder.id) ?? manualApprovalFieldsFromRow(null)),
  };
  const assignments = await listStoreOrderCourierAssignments(client, typedOrder.store_id, [
    typedOrder.id,
  ]);

  return {
    order: adminOrder,
    items: (rawItems ?? []) as unknown as OrderItem[],
    events: (rawEvents ?? []) as unknown as OrderEvent[],
    courier_assignment: assignments.get(typedOrder.id) ?? null,
  };
}

async function listManualApprovalFields(
  client: SupabaseClient,
  storeId: string,
  orderIds: readonly string[],
): Promise<Map<string, ManualApprovalFields>> {
  if (orderIds.length === 0) return new Map();
  const { data, error } = await client
    .from('order_manual_approvals')
    .select(
      'order_id, status, required_at, approved_at, approved_by_user_id, declined_at, declined_by_user_id',
    )
    .eq('store_id', storeId)
    .in('order_id', [...new Set(orderIds)])
    .is('deleted_at', null);
  if (error) throw error;
  return new Map(
    ((data ?? []) as ManualApprovalRow[]).map((row) => [
      row.order_id,
      manualApprovalFieldsFromRow(row),
    ]),
  );
}

async function listStoreOrderCourierAssignments(
  client: SupabaseClient,
  storeId: string,
  orderIds: readonly string[],
): Promise<Map<string, CourierOrderAssignmentSummary>> {
  if (orderIds.length === 0) return new Map();
  const maybeClient = client as unknown as { readonly rpc?: unknown };
  if (typeof maybeClient.rpc !== 'function') return new Map();
  const { data, error } = await client.rpc('list_store_order_courier_assignments', {
    target_store_id: storeId,
    target_order_ids: [...new Set(orderIds)],
  });
  if (error) {
    const maybe = error as { readonly code?: string };
    if (maybe.code === '42501') return new Map();
    throw error;
  }
  const rows = (data ?? []) as Array<
    CourierOrderAssignmentSummary & {
      readonly order_id: string;
    }
  >;
  return new Map(
    rows.map((row) => [
      row.order_id,
      {
        batch_id: row.batch_id,
        batch_order_id: row.batch_order_id,
        display_code: row.display_code,
        package_tag: row.package_tag,
        batch_order_count: row.batch_order_count,
        courier_id: row.courier_id,
        courier_name: row.courier_name,
        courier_phone_number: row.courier_phone_number,
        assigned_at: row.assigned_at,
        link_expires_at: row.link_expires_at,
        link_revoked_at: row.link_revoked_at,
        proof_status: row.proof_status,
        latest_proof_id: row.latest_proof_id,
        latest_proof_submitted_at: row.latest_proof_submitted_at,
        proof_admin_confirmed_at: row.proof_admin_confirmed_at,
      },
    ]),
  );
}
